import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { LatestIncidentReport } from '../../pages/incidentsList/incidentsList.interface';
import { dateTime } from '../../time/datetime';
import { currentLocaleSports } from '../../locale/LocaleConfig';
import { useCustomTranslation } from '../../translation/customTranslation';
import {
  createLocalisation,
  LocalisationProps,
} from '../../translation/helpers/enumLocalisation';
import {
  MechanismOfInjury,
  PlayType,
  SessionType,
  SportType,
} from '../../pages/createIncident/smart/completeIncident.enum';

export interface IncidentProps {
  incident: LatestIncidentReport;
  player?: string | undefined;
  isCoaches?: boolean;
}

const sessionTypeList: LocalisationProps[] = Object.values(
  createLocalisation(SessionType, 'incident.sessionType'),
);
const playTypeList: LocalisationProps[] = Object.values(
  createLocalisation(PlayType, 'incident.playType'),
);
const headImpactTypeList: LocalisationProps[] = Object.values(
  createLocalisation(MechanismOfInjury, 'incident.mechanismOfInjury'),
);

export function IncidentReport({ incident, player, isCoaches }: IncidentProps) {
  const { t } = useTranslation();
  const { t: tCustom } = useCustomTranslation();

  const activityLocalised = tCustom(
    currentLocaleSports.find((act) => act.value === incident.report?.activity)
      ?.i18nKey || '',
  );
  const sessionTypeLocalised = tCustom(
    sessionTypeList.find((act) => act.value === incident.report?.sessionType)
      ?.i18nKey || '',
  );
  const playTypeLocalised = tCustom(
    playTypeList.find((act) => act.value === incident.report?.playType)
      ?.i18nKey || '',
  );
  const headImpactTypeLocalised = tCustom(
    headImpactTypeList.find(
      (act) => act.value === incident.report?.headImpactType,
    )?.i18nKey || '',
  );

  return (
    <Grid sx={{ mx: 2 }}>
      <Grid container justifyContent="space-between">
        <Grid item sx={{ my: 1 }}>
          <Typography variant="subtitle1">
            {t('incident.INCIDENT_REPORT_ROW_TITLE_DATE')}
          </Typography>
        </Grid>
        <Grid item sx={{ my: 1 }} data-testid="viewIncidentDateTime">
          {dateTime.format(
            incident.incidentDateTime,
            t('common.datetime.FORMAT_SHORT_DATE'),
          )}
        </Grid>
      </Grid>
      <Grid container justifyContent="space-between">
        <Grid item sx={{ my: 1 }}>
          <Typography variant="subtitle1">
            {t('incident.INCIDENT_REPORT_ROW_TITLE_TIME')}
          </Typography>
        </Grid>
        <Grid item sx={{ my: 1 }}>
          <Typography variant="subtitle1">
            {dateTime.format(
              incident.incidentDateTime,
              t('common.datetime.FORMAT_STANDARD_SHORT_TIME'),
            )}
          </Typography>
        </Grid>
      </Grid>
      <Grid container justifyContent="space-between">
        <Grid item sx={{ my: 1 }}>
          <Typography variant="subtitle1">
            {t('incident.INCIDENT_REPORT_ROW_TITLE_RED_FLAGS')}
          </Typography>
        </Grid>
        <Grid item sx={{ my: 1 }}>
          <Typography variant="subtitle1">
            {incident.report?.redFlags
              ? t('common.generic.GENERIC_CONFIRMATION_AFFIRMATIVE')
              : t('common.generic.GENERIC_CONFIRMATION_NEGATIVE')}
          </Typography>
        </Grid>
      </Grid>
      {incident.report?.activity && (
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {t('incident.sport.ACTIVITY_TITLE')}:
            </Typography>
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {incident.report?.activity &&
              incident.report?.activity === SportType.OTHER &&
              incident.report?.activityOther
                ? incident.report?.activityOther
                : activityLocalised}
            </Typography>
          </Grid>
        </Grid>
      )}
      {incident.report?.sessionType && (
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {t('incident.sessionType.SESSION_TYPE_TITLE')}:
            </Typography>
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {incident.report?.sessionType &&
              incident.report?.sessionType === SessionType.OTHER &&
              incident.report?.sessionTypeOther
                ? incident.report?.sessionTypeOther
                : sessionTypeLocalised}
            </Typography>
          </Grid>
        </Grid>
      )}
      {incident.report?.playType && (
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {t('incident.playType.PLAY_TYPE_TITLE')}:
            </Typography>
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {' '}
              {incident.report?.playType &&
              incident.report?.playType === PlayType.OTHER &&
              incident.report?.playTypeOther
                ? incident.report?.playTypeOther
                : playTypeLocalised}
            </Typography>
          </Grid>
        </Grid>
      )}
      {incident.report?.headImpactType && (
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {' '}
              {t('incident.mechanismOfInjury.HEAD_IMPACT_TYPE_TITLE')}:
            </Typography>
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography variant="subtitle1">
              {incident.report?.headImpactType &&
              incident.report?.headImpactType === MechanismOfInjury.OTHER &&
              incident.report?.headImpactTypeOther
                ? incident.report?.headImpactTypeOther
                : headImpactTypeLocalised}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
